/**
 * dnsFilterProfiles -- the curated DNS-layer content-filtering choices for
 * Gateway > Content Filtering.
 *
 * This MIRRORS services/dns_filter_catalog.py on the backend. Content Filtering v1
 * adds NO new API: each profile is just a `use-dns` IP list written to a LAN
 * network's DHCP scope through the EXISTING `dhcp.scope` gateway intent -- the same
 * field the DHCP screen's "Specify DNS servers" control writes. Keep the two files in
 * step if the resolver IPs change.
 *
 * PLACEHOLDER RESOLVERS: `family` / `malware` point at the public CleanBrowsing free
 * filters so the feature works end to end; the owner should confirm or replace them.
 *
 * "off" is an EMPTY list on purpose. Empty `use-dns` means clients use the GATEWAY as
 * their resolver -- it is NOT "filtering disabled everywhere", only "no explicit
 * filtering resolver set on this network". UI copy must never conflate the two.
 * "custom" carries `dns: null`: the operator supplies the addresses themselves.
 */

/** The four curated choices. `off`/`family`/`malware` fix a `use-dns` list; `custom` does not. */
export type DnsFilterProfileId = "off" | "family" | "malware" | "custom";

export interface DnsFilterProfile {
  id: DnsFilterProfileId;
  /** Picker label. */
  label: string;
  /** One-line description shown under the picker. */
  hint: string;
  /** The exact list written to the scope's use-dns: [] = off, null = custom (operator-supplied). */
  dns: string[] | null;
}

/** Ordered for the picker; mirrors PROFILES/PROFILE_LABELS in dns_filter_catalog.py. */
export const DNS_FILTER_PROFILES: DnsFilterProfile[] = [
  {
    id: "off",
    label: "Off (use the gateway as resolver)",
    hint: "No filtering resolver is set. Clients resolve through the gateway itself.",
    dns: [],
  },
  {
    id: "family",
    label: "Family",
    hint: "CleanBrowsing Family filter: blocks adult content plus malware/phishing (placeholder).",
    dns: ["185.228.168.168", "185.228.169.168"],
  },
  {
    id: "malware",
    label: "Malware & phishing",
    hint: "CleanBrowsing Security filter: blocks malware/phishing only (placeholder).",
    dns: ["185.228.168.9", "185.228.169.9"],
  },
  {
    id: "custom",
    label: "Custom DNS servers",
    hint: "Enter your own filtering resolvers below.",
    dns: null,
  },
];

/** The resolver IPs a profile writes: [] for "off", null for "custom". */
export function dnsForProfile(id: DnsFilterProfileId): string[] | null {
  const profile = DNS_FILTER_PROFILES.find((p) => p.id === id);
  // `off` is [] (falsy-safe: [] is not nullish, so ?? keeps it); an unknown id -> null.
  return profile ? profile.dns : null;
}

/**
 * Which profile a scope's current `use-dns` list represents.
 *   empty                          -> "off"
 *   exact (order-insensitive) pair -> that curated profile
 *   anything else set by hand      -> "custom"
 */
export function matchDnsFilterProfile(useDns: string[] | null | undefined): DnsFilterProfileId {
  const list = (useDns ?? []).filter(Boolean);
  if (list.length === 0) return "off";
  const key = [...list].sort().join(",");
  for (const p of DNS_FILTER_PROFILES) {
    if (p.dns && p.dns.length > 0 && [...p.dns].sort().join(",") === key) return p.id;
  }
  return "custom";
}
